import type { NavItemProps } from '../types';

import { mergeClasses } from 'minimal-shared/utils';

import { Link } from '@/components/ui';
import { Iconify } from '@/components/iconify';

import { createNavItem, navSectionClasses } from '../components/nav-section-classes';

export const NavItem = ({
  title,
  path,
  icon,
  /********/
  open,
  active,
  /********/
  subItem,
  subHeader,
  hasChild,
  className,
  externalLink,
  color,
  ...other
}: NavItemProps) => {
  const navItem = createNavItem({ path, hasChild, externalLink });

  const variant = !subItem && !subHeader ? 'rootItem' : subHeader ? 'subHeader' : 'subItem';

  return (
    <Link
      aria-label={title}
      target={externalLink ? '_blank' : undefined}
      {...navItem.baseProps}
      color="inherit"
      className={mergeClasses(
        [
          navSectionClasses.item.root,
          'flex items-center no-underline text-lg relative py-[4px] transition-colors duration-200 ease-in-out cursor-pointer hover:no-underline',
          // Root item styles
          ...(variant === 'rootItem'
            ? [
                'text-black uppercase rounded-md hover:bg-primary hover:text-white px-[16px] py-[8px]',
                active ? 'bg-primary text-white rounded-md' : undefined,
                open
                  ? 'bg-primary text-white rounded-md before:opacity-100 before:scale-100'
                  : undefined,
              ]
            : [undefined]),
          // Sub item styles
          ...(variant === 'subItem'
            ? [
                'text-gray-500 hover:text-gray-900',
                active ? 'text-gray-900 font-semibold' : undefined,
              ]
            : [undefined]),
          ...(variant === 'subHeader'
            ? ['font-bold text-black hover:text-primary', active ? 'text-primary' : undefined]
            : [undefined]),
          className,
        ],
        {
          [navSectionClasses.state.open]: open,
          [navSectionClasses.state.active]: active,
        }
      )}
      {...other}
    >
      {icon && (
        <span className="flex mr-2 flex-shrink-0">
          {typeof icon === 'string' ? (
            <Iconify
              icon={icon as any}
              className={mergeClasses(['text-secondary', ...(active ? ['text-secondary'] : [])])}
              width={20}
              height={20}
            />
          ) : (
            icon
          )}
        </span>
      )}

      <span
        className={mergeClasses([
          navSectionClasses.item.title,
          'flex-grow',
          variant === 'subItem' ? '!text-sm' : 'text-sm',
          variant === 'subHeader' ? '!text-lg' : 'text-sm',
          active ? 'font-semibold' : 'font-medium',
        ])}
      >
        {title}
      </span>

      {hasChild && (
        <span
          className={mergeClasses([
            navSectionClasses.item.arrow,
            'ml-1 flex items-center justify-center transition-transform duration-200 ease-in-out',
          ])}
        >
          <Iconify icon="lucide:chevron-down" width={20} height={20} />
        </span>
      )}
    </Link>
  );
};
